/**
 * FinanceApp - Module E-commerce
 * Copyright (c) 2026 Emmanuel TESTE
 * Connecteurs Shopify / WooCommerce / PrestaShop — 6 onglets
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import api from "../services/api";
import {
  ShoppingBag, BarChart2, Settings, Package, Activity, AlertCircle,
  Plus, Trash2, RefreshCw, CheckCircle, XCircle, Clock, ExternalLink,
  Eye, EyeOff, ChevronRight, Store, Zap, TrendingUp, ShoppingCart,
  AlertTriangle, Box, FileText, Wifi, WifiOff, RotateCcw,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart,
  Line, CartesianGrid, Legend,
} from "recharts";

// ── Onglets ───────────────────────────────────────────────────────────────────
const TABS = [
  { id: "dashboard",  label: "Tableau de bord",      Icon: BarChart2 },
  { id: "shops",      label: "Boutiques",             Icon: Store },
  { id: "orders",     label: "Commandes",             Icon: ShoppingCart },
  { id: "products",   label: "Produits & Stock",      Icon: Package },
  { id: "stats",      label: "Statistiques",          Icon: TrendingUp },
  { id: "logs",       label: "Logs & Monitoring",     Icon: Activity },
];

const PLATFORMS = {
  shopify:      { label: "Shopify",      color: "#96BF48", bgColor: "#F6F6F1" },
  woocommerce:  { label: "WooCommerce",  color: "#7F54B3", bgColor: "#F6F3FB" },
  prestashop:   { label: "PrestaShop",   color: "#DF0067", bgColor: "#FEF0F5" },
};

const STATUS_COLORS = {
  connecté:     "#10B981",
  déconnecté:   "#6B7280",
  erreur:       "#EF4444",
  en_attente:   "#F59E0B",
};

// ── Styles communs ────────────────────────────────────────────────────────────
const card = {
  background: "var(--bg-card, #fff)",
  border: "1px solid var(--border, #e5e7eb)",
  borderRadius: 12,
  padding: 20,
};
const kpiCard = {
  ...card,
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

// ── Formatage ─────────────────────────────────────────────────────────────────
const fmt = n => n == null ? "—" : new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n);
const fmtDate = d => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
const fmtDateShort = d => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "—";

// ── Composant badge statut ────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || "#6B7280";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: color + "22", color }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: color, display: "inline-block" }} />
      {status}
    </span>
  );
}

// ── Composant logo plateforme ─────────────────────────────────────────────────
function PlatformLogo({ platform, size = 32 }) {
  const p = PLATFORMS[platform];
  if (!p) return null;
  return (
    <div style={{ width: size, height: size, borderRadius: 8, background: p.bgColor, border: `2px solid ${p.color}33`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <span style={{ fontSize: size * 0.4, fontWeight: 800, color: p.color }}>{p.label[0]}</span>
    </div>
  );
}

// ── Onglet Dashboard ──────────────────────────────────────────────────────────
function TabDashboard({ stats, shops, onSyncAll }) {
  if (!stats) return <div style={{ textAlign: "center", padding: 40, color: "#6B7280" }}>Chargement...</div>;

  const kpis = [
    { label: "Boutiques connectées", value: stats.shops_connected, Icon: Store, color: "#6366F1" },
    { label: "Commandes aujourd'hui", value: stats.orders_today, Icon: ShoppingCart, color: "#10B981" },
    { label: "CA total", value: fmt(stats.total_ca), Icon: TrendingUp, color: "#F59E0B" },
    { label: "En attente traitement", value: stats.orders_pending, Icon: AlertCircle, color: "#EF4444" },
  ];

  const chartData = (stats.ca_last_30_days || []).map(d => ({ date: fmtDateShort(d.day), CA: Math.round(d.ca) }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
        {kpis.map(({ label, value, Icon, color }) => (
          <div key={label} style={kpiCard}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: color + "22", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon size={18} color={color} />
              </div>
              <span style={{ fontSize: 13, color: "#6B7280" }}>{label}</span>
            </div>
            <span style={{ fontSize: 26, fontWeight: 700 }}>{value ?? "—"}</span>
          </div>
        ))}
      </div>

      {/* Graphique CA 30 jours */}
      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Évolution CA — 30 derniers jours</h3>
          <button onClick={onSyncAll} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px solid #6366F1", color: "#6366F1", background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
            <RefreshCw size={14} /> Sync tout
          </button>
        </div>
        {chartData.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: "#9CA3AF" }}>Pas encore de données</div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Line type="monotone" dataKey="CA" stroke="#6366F1" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* CA par boutique */}
      {(stats.ca_by_shop || []).length > 0 && (
        <div style={card}>
          <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 600 }}>CA par boutique</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={stats.ca_by_shop}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="shop_name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Bar dataKey="ca" fill="#6366F1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Alertes */}
      {shops.some(s => s.status === "erreur") && (
        <div style={{ ...card, border: "1px solid #FCA5A5", background: "#FEF2F2", display: "flex", alignItems: "center", gap: 10 }}>
          <AlertTriangle size={18} color="#EF4444" />
          <span style={{ fontSize: 14, color: "#991B1B" }}>
            {shops.filter(s => s.status === "erreur").length} boutique(s) en erreur de synchronisation.
          </span>
        </div>
      )}
    </div>
  );
}

// ── Formulaire connexion boutique (4 étapes) ──────────────────────────────────
function ShopForm({ onSave, onCancel }) {
  const { showToast } = useToast();
  const [step, setStep]     = useState(1);
  const [testing, setTest]  = useState(false);
  const [form, setForm]     = useState({
    platform: "", shop_name: "", shop_url: "",
    api_key: "", api_secret: "", access_token: "", webhook_secret: "",
    sync_products: true, sync_orders: true, sync_stock: true, sync_customers: false,
    sync_frequency_minutes: 15,
  });
  const [showKeys, setShowKeys] = useState({});

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleKey = k => setShowKeys(s => ({ ...s, [k]: !s[k] }));

  async function handleTest() {
    setTest(true);
    try {
      // On passe skip_test=false pour que le backend teste la connexion
      const res = await api.post("/ecommerce/shops", { ...form, skip_test: false });
      if (res.data?.id || res.status === 201) {
        showToast("Boutique connectée avec succès !", "success");
        onSave();
      }
    } catch (err) {
      const msg = err.response?.data?.error || "Erreur de connexion";
      showToast(msg, "error");
    } finally { setTest(false); }
  }

  return (
    <div style={{ ...card, maxWidth: 560 }}>
      {/* Stepper */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        {["Plateforme", "Credentials", "Paramètres", "Connexion"].map((s, i) => (
          <div key={s} style={{ display: "flex", alignItems: "center", gap: 6, flex: 1 }}>
            <div style={{ width: 24, height: 24, borderRadius: "50%", background: step > i ? "#6366F1" : step === i + 1 ? "#6366F1" : "#E5E7EB", color: step >= i + 1 ? "#fff" : "#9CA3AF", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
              {step > i + 1 ? "✓" : i + 1}
            </div>
            <span style={{ fontSize: 12, color: step === i + 1 ? "#6366F1" : "#9CA3AF", fontWeight: step === i + 1 ? 600 : 400 }}>{s}</span>
            {i < 3 && <ChevronRight size={14} color="#D1D5DB" style={{ flexShrink: 0 }} />}
          </div>
        ))}
      </div>

      {/* Étape 1 — Plateforme */}
      {step === 1 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <h3 style={{ margin: 0 }}>Choisir la plateforme</h3>
          {Object.entries(PLATFORMS).map(([key, p]) => (
            <button key={key} onClick={() => set("platform", key)} style={{ display: "flex", alignItems: "center", gap: 14, padding: 14, borderRadius: 10, border: `2px solid ${form.platform === key ? p.color : "#E5E7EB"}`, background: form.platform === key ? p.bgColor : "#fff", cursor: "pointer", transition: "all 0.15s" }}>
              <PlatformLogo platform={key} size={40} />
              <div style={{ textAlign: "left" }}>
                <div style={{ fontWeight: 700, color: p.color }}>{p.label}</div>
                <div style={{ fontSize: 12, color: "#6B7280" }}>
                  {key === "shopify" && "Access Token via API privée"}
                  {key === "woocommerce" && "Consumer Key + Consumer Secret"}
                  {key === "prestashop" && "Clé API WebService"}
                </div>
              </div>
              {form.platform === key && <CheckCircle size={20} color={p.color} style={{ marginLeft: "auto" }} />}
            </button>
          ))}
          {form.platform && (
            <div style={{ display: "flex", gap: 8 }}>
              <input type="text" placeholder="Nom de la boutique" value={form.shop_name} onChange={e => set("shop_name", e.target.value)} style={{ flex: 1, padding: "8px 12px", borderRadius: 8, border: "1px solid #D1D5DB", fontSize: 14 }} />
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button onClick={onCancel} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #D1D5DB", background: "#fff", cursor: "pointer" }}>Annuler</button>
            <button onClick={() => setStep(2)} disabled={!form.platform || !form.shop_name.trim()} style={{ padding: "8px 16px", borderRadius: 8, background: "#6366F1", color: "#fff", border: "none", cursor: "pointer", opacity: !form.platform || !form.shop_name.trim() ? 0.5 : 1 }}>Suivant →</button>
          </div>
        </div>
      )}

      {/* Étape 2 — Credentials */}
      {step === 2 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <h3 style={{ margin: 0 }}>Paramètres de connexion</h3>
          <p style={{ margin: 0, fontSize: 13, color: "#6B7280" }}>⚠️ Ces informations sont chiffrées AES-256-GCM et ne seront jamais affichées après sauvegarde.</p>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 13, fontWeight: 600 }}>URL de la boutique *</label>
            <input type="url" placeholder="https://ma-boutique.myshopify.com" value={form.shop_url} onChange={e => set("shop_url", e.target.value)} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #D1D5DB", fontSize: 14 }} />
            <span style={{ fontSize: 11, color: "#9CA3AF" }}>HTTPS obligatoire — URLs locales non autorisées</span>
          </div>

          {(form.platform === "shopify") && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Access Token *</label>
              <div style={{ position: "relative" }}>
                <input type={showKeys.token ? "text" : "password"} placeholder="shpat_..." value={form.access_token} onChange={e => set("access_token", e.target.value)} style={{ width: "100%", padding: "8px 36px 8px 12px", borderRadius: 8, border: "1px solid #D1D5DB", fontSize: 14, boxSizing: "border-box" }} />
                <button onClick={() => toggleKey("token")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer" }}>{showKeys.token ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
            </div>
          )}

          {(form.platform === "woocommerce" || form.platform === "prestashop") && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Clé API (Consumer Key) *</label>
              <div style={{ position: "relative" }}>
                <input type={showKeys.key ? "text" : "password"} placeholder={form.platform === "woocommerce" ? "ck_..." : "Clé API"} value={form.api_key} onChange={e => set("api_key", e.target.value)} style={{ width: "100%", padding: "8px 36px 8px 12px", borderRadius: 8, border: "1px solid #D1D5DB", fontSize: 14, boxSizing: "border-box" }} />
                <button onClick={() => toggleKey("key")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer" }}>{showKeys.key ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
            </div>
          )}

          {form.platform === "woocommerce" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Consumer Secret *</label>
              <div style={{ position: "relative" }}>
                <input type={showKeys.secret ? "text" : "password"} placeholder="cs_..." value={form.api_secret} onChange={e => set("api_secret", e.target.value)} style={{ width: "100%", padding: "8px 36px 8px 12px", borderRadius: 8, border: "1px solid #D1D5DB", fontSize: 14, boxSizing: "border-box" }} />
                <button onClick={() => toggleKey("secret")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer" }}>{showKeys.secret ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              </div>
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 13, fontWeight: 600 }}>Secret Webhook (optionnel)</label>
            <div style={{ position: "relative" }}>
              <input type={showKeys.webhook ? "text" : "password"} placeholder="Secret pour valider les webhooks entrants" value={form.webhook_secret} onChange={e => set("webhook_secret", e.target.value)} style={{ width: "100%", padding: "8px 36px 8px 12px", borderRadius: 8, border: "1px solid #D1D5DB", fontSize: 14, boxSizing: "border-box" }} />
              <button onClick={() => toggleKey("webhook")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer" }}>{showKeys.webhook ? <EyeOff size={16} /> : <Eye size={16} />}</button>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <button onClick={() => setStep(1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #D1D5DB", background: "#fff", cursor: "pointer" }}>← Retour</button>
            <button onClick={() => setStep(3)} disabled={!form.shop_url} style={{ padding: "8px 16px", borderRadius: 8, background: "#6366F1", color: "#fff", border: "none", cursor: "pointer", opacity: !form.shop_url ? 0.5 : 1 }}>Suivant →</button>
          </div>
        </div>
      )}

      {/* Étape 3 — Paramètres sync */}
      {step === 3 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <h3 style={{ margin: 0 }}>Paramètres de synchronisation</h3>
          {[
            { key: "sync_products", label: "Produits", desc: "Synchroniser le catalogue produits" },
            { key: "sync_orders",   label: "Commandes", desc: "Importer les commandes automatiquement" },
            { key: "sync_stock",    label: "Stock",     desc: "Mettre à jour les niveaux de stock" },
            { key: "sync_customers",label: "Clients",   desc: "Importer les données clients (RGPD)" },
          ].map(({ key, label, desc }) => (
            <label key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", padding: "10px 14px", borderRadius: 8, border: "1px solid #E5E7EB" }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{label}</div>
                <div style={{ fontSize: 12, color: "#6B7280" }}>{desc}</div>
              </div>
              <input type="checkbox" checked={form[key]} onChange={e => set(key, e.target.checked)} style={{ width: 16, height: 16, cursor: "pointer" }} />
            </label>
          ))}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={{ fontSize: 13, fontWeight: 600 }}>Fréquence de sync</label>
            <select value={form.sync_frequency_minutes} onChange={e => set("sync_frequency_minutes", parseInt(e.target.value))} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #D1D5DB", fontSize: 14 }}>
              {[5, 15, 30, 60].map(m => <option key={m} value={m}>Toutes les {m} minutes</option>)}
            </select>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <button onClick={() => setStep(2)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #D1D5DB", background: "#fff", cursor: "pointer" }}>← Retour</button>
            <button onClick={() => setStep(4)} style={{ padding: "8px 16px", borderRadius: 8, background: "#6366F1", color: "#fff", border: "none", cursor: "pointer" }}>Suivant →</button>
          </div>
        </div>
      )}

      {/* Étape 4 — Connexion */}
      {step === 4 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <h3 style={{ margin: 0 }}>Tester et connecter</h3>
          <div style={{ background: "#F8F9FA", borderRadius: 10, padding: 16 }}>
            <table style={{ width: "100%", fontSize: 13 }}>
              <tbody>
                <tr><td style={{ color: "#6B7280", paddingBottom: 6 }}>Plateforme</td><td style={{ fontWeight: 600 }}>{PLATFORMS[form.platform]?.label}</td></tr>
                <tr><td style={{ color: "#6B7280", paddingBottom: 6 }}>Boutique</td><td style={{ fontWeight: 600 }}>{form.shop_name}</td></tr>
                <tr><td style={{ color: "#6B7280", paddingBottom: 6 }}>URL</td><td style={{ fontWeight: 600, wordBreak: "break-all" }}>{form.shop_url}</td></tr>
                <tr><td style={{ color: "#6B7280" }}>Sync</td><td style={{ fontWeight: 600 }}>{[form.sync_orders && "commandes", form.sync_products && "produits", form.sync_stock && "stock"].filter(Boolean).join(", ")}</td></tr>
              </tbody>
            </table>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: "#6B7280" }}>
            En cliquant sur "Connecter", FinanceApp va tester la connexion et chiffrer vos credentials. En cas d'erreur, un message générique sera affiché.
          </p>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <button onClick={() => setStep(3)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #D1D5DB", background: "#fff", cursor: "pointer" }}>← Retour</button>
            <button onClick={handleTest} disabled={testing} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 20px", borderRadius: 8, background: "#10B981", color: "#fff", border: "none", cursor: testing ? "wait" : "pointer", opacity: testing ? 0.7 : 1, fontWeight: 600 }}>
              {testing ? <RefreshCw size={14} style={{ animation: "spin 1s linear infinite" }} /> : <Zap size={14} />}
              {testing ? "Connexion en cours..." : "Tester et connecter"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Onglet Boutiques ──────────────────────────────────────────────────────────
function TabShops({ shops, onRefresh, onDelete, onSync, onTest }) {
  const [showForm, setShowForm] = useState(false);

  if (showForm) {
    return <ShopForm onSave={() => { setShowForm(false); onRefresh(); }} onCancel={() => setShowForm(false)} />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>Boutiques connectées</h2>
        <button onClick={() => setShowForm(true)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, background: "#6366F1", color: "#fff", border: "none", cursor: "pointer", fontWeight: 600 }}>
          <Plus size={16} /> Connecter une boutique
        </button>
      </div>

      {shops.length === 0 ? (
        <div style={{ ...card, textAlign: "center", padding: 60 }}>
          <Store size={48} color="#D1D5DB" style={{ marginBottom: 16 }} />
          <p style={{ color: "#6B7280", margin: 0 }}>Aucune boutique connectée.<br />Connectez votre première boutique Shopify, WooCommerce ou PrestaShop.</p>
        </div>
      ) : shops.map(shop => (
        <div key={shop.id} style={{ ...card, display: "flex", alignItems: "center", gap: 16 }}>
          <PlatformLogo platform={shop.platform} size={44} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 700, fontSize: 16 }}>{shop.shop_name}</span>
              <StatusBadge status={shop.status} />
            </div>
            <div style={{ fontSize: 13, color: "#6B7280", marginTop: 2 }}>{shop.shop_url}</div>
            <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 4 }}>
              Dernière sync : {fmtDate(shop.last_sync_at)} · {shop.last_sync_status || "—"}
              {shop.sync_error_count > 0 && <span style={{ color: "#EF4444", marginLeft: 8 }}>⚠ {shop.sync_error_count} erreur(s)</span>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            <button onClick={() => onTest(shop.id)} title="Tester connexion" style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #D1D5DB", background: "#fff", cursor: "pointer" }}>
              <Wifi size={14} color="#6B7280" />
            </button>
            <button onClick={() => onSync(shop.id, "orders")} title="Sync commandes" style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #6366F1", background: "#F5F3FF", cursor: "pointer" }}>
              <RefreshCw size={14} color="#6366F1" />
            </button>
            <button onClick={() => onDelete(shop.id, shop.shop_name)} title="Déconnecter" style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #FCA5A5", background: "#FEF2F2", cursor: "pointer" }}>
              <Trash2 size={14} color="#EF4444" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Onglet Commandes ──────────────────────────────────────────────────────────
function TabOrders({ shops }) {
  const [orders, setOrders]   = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [shopId, setShopId]   = useState("");
  const [page, setPage]       = useState(1);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, limit: LIMIT });
      if (shopId) p.set("shop_id", shopId);
      const r = await api.get(`/ecommerce/orders?${p}`);
      setOrders(r.data.orders || []);
      setTotal(r.data.total || 0);
    } catch { setOrders([]); }
    finally { setLoading(false); }
  }, [page, shopId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <h2 style={{ margin: 0, fontSize: 18, flex: 1 }}>Commandes importées <span style={{ fontSize: 14, color: "#6B7280", fontWeight: 400 }}>({total})</span></h2>
        <select value={shopId} onChange={e => { setShopId(e.target.value); setPage(1); }} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #D1D5DB", fontSize: 13 }}>
          <option value="">Toutes les boutiques</option>
          {shops.map(s => <option key={s.id} value={s.id}>{s.shop_name}</option>)}
        </select>
        <button onClick={load} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #D1D5DB", background: "#fff", cursor: "pointer" }}><RefreshCw size={14} /></button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#9CA3AF" }}>Chargement...</div>
      ) : orders.length === 0 ? (
        <div style={{ ...card, textAlign: "center", padding: 40 }}>
          <ShoppingCart size={40} color="#D1D5DB" style={{ marginBottom: 12 }} />
          <p style={{ color: "#6B7280", margin: 0 }}>Aucune commande importée</p>
        </div>
      ) : (
        <div style={{ ...card, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #F3F4F6" }}>
                {["Boutique", "N° commande", "Client", "Montant", "Statut", "Importée le"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "8px 12px", color: "#6B7280", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id} style={{ borderBottom: "1px solid #F9FAFB" }}>
                  <td style={{ padding: "10px 12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <PlatformLogo platform={o.platform} size={24} />
                      <span>{o.shop_name}</span>
                    </div>
                  </td>
                  <td style={{ padding: "10px 12px", fontWeight: 600 }}>#{o.platform_order_number || o.platform_order_id}</td>
                  <td style={{ padding: "10px 12px" }}>{o.customer_name || o.customer_email || "—"}</td>
                  <td style={{ padding: "10px 12px", fontWeight: 600 }}>{fmt(o.total_price)}</td>
                  <td style={{ padding: "10px 12px" }}>
                    <span style={{ padding: "2px 8px", borderRadius: 4, background: "#F3F4F6", fontSize: 12 }}>{o.status}</span>
                  </td>
                  <td style={{ padding: "10px 12px", color: "#9CA3AF" }}>{fmtDate(o.synced_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {total > LIMIT && (
        <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #D1D5DB", background: "#fff", cursor: "pointer" }}>←</button>
          <span style={{ padding: "6px 12px", fontSize: 13, color: "#6B7280" }}>{page} / {Math.ceil(total / LIMIT)}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={page >= Math.ceil(total / LIMIT)} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #D1D5DB", background: "#fff", cursor: "pointer" }}>→</button>
        </div>
      )}
    </div>
  );
}

// ── Onglet Produits ───────────────────────────────────────────────────────────
function TabProducts({ shops }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [shopId, setShopId]     = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: 100 });
      if (shopId) p.set("shop_id", shopId);
      const r = await api.get(`/ecommerce/products?${p}`);
      setProducts(r.data.products || []);
    } catch { setProducts([]); }
    finally { setLoading(false); }
  }, [shopId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <h2 style={{ margin: 0, fontSize: 18, flex: 1 }}>Mapping produits</h2>
        <select value={shopId} onChange={e => setShopId(e.target.value)} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #D1D5DB", fontSize: 13 }}>
          <option value="">Toutes les boutiques</option>
          {shops.map(s => <option key={s.id} value={s.id}>{s.shop_name}</option>)}
        </select>
        <button onClick={load} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #D1D5DB", background: "#fff", cursor: "pointer" }}><RefreshCw size={14} /></button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#9CA3AF" }}>Chargement...</div>
      ) : products.length === 0 ? (
        <div style={{ ...card, textAlign: "center", padding: 40 }}>
          <Box size={40} color="#D1D5DB" style={{ marginBottom: 12 }} />
          <p style={{ color: "#6B7280", margin: 0 }}>Aucun produit synchronisé</p>
        </div>
      ) : (
        <div style={{ ...card, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #F3F4F6" }}>
                {["Boutique", "ID Plateforme", "Produit FinanceApp", "SKU", "Stock", "Dernière sync", "Direction"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "8px 12px", color: "#6B7280", fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id} style={{ borderBottom: "1px solid #F9FAFB" }}>
                  <td style={{ padding: "10px 12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <PlatformLogo platform={p.platform} size={20} />
                      <span style={{ fontSize: 12 }}>{p.shop_name}</span>
                    </div>
                  </td>
                  <td style={{ padding: "10px 12px", fontFamily: "monospace", fontSize: 12 }}>{p.platform_product_id}</td>
                  <td style={{ padding: "10px 12px" }}>{p.financeapp_name || <span style={{ color: "#D1D5DB" }}>Non mappé</span>}</td>
                  <td style={{ padding: "10px 12px", fontFamily: "monospace", fontSize: 12 }}>{p.sku || "—"}</td>
                  <td style={{ padding: "10px 12px" }}>
                    {p.stock_current != null ? (
                      <span style={{ color: p.stock_current <= 0 ? "#EF4444" : p.stock_current <= 5 ? "#F59E0B" : "#10B981", fontWeight: 600 }}>{p.stock_current}</span>
                    ) : "—"}
                  </td>
                  <td style={{ padding: "10px 12px", color: "#9CA3AF" }}>{fmtDate(p.last_synced_at)}</td>
                  <td style={{ padding: "10px 12px" }}>
                    <span style={{ padding: "2px 8px", borderRadius: 4, background: "#F3F4F6", fontSize: 11 }}>{p.sync_direction}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Onglet Statistiques ───────────────────────────────────────────────────────
function TabStats({ stats }) {
  if (!stats) return <div style={{ textAlign: "center", padding: 40, color: "#9CA3AF" }}>Chargement...</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <h2 style={{ margin: 0, fontSize: 18 }}>Statistiques e-commerce</h2>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
        {[
          { label: "Total commandes",   value: stats.total_orders, color: "#6366F1" },
          { label: "CA total",          value: fmt(stats.total_ca), color: "#10B981" },
          { label: "Panier moyen",      value: stats.total_orders > 0 ? fmt(stats.total_ca / stats.total_orders) : "—", color: "#F59E0B" },
          { label: "Boutiques actives", value: stats.shops_connected, color: "#8B5CF6" },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ ...card, textAlign: "center" }}>
            <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 8 }}>{label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* CA par boutique — bar chart */}
      {(stats.ca_by_shop || []).length > 0 && (
        <div style={card}>
          <h3 style={{ margin: "0 0 16px", fontSize: 15 }}>CA par boutique</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={stats.ca_by_shop} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => fmt(v)} />
              <YAxis dataKey="shop_name" type="category" tick={{ fontSize: 12 }} width={120} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Bar dataKey="ca" fill="#6366F1" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Commandes par boutique */}
      {(stats.ca_by_shop || []).length > 0 && (
        <div style={card}>
          <h3 style={{ margin: "0 0 16px", fontSize: 15 }}>Commandes par boutique</h3>
          <div style={{ display: "grid", gap: 10 }}>
            {stats.ca_by_shop.map(s => (
              <div key={s.shop_name} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ width: 120, fontSize: 13, fontWeight: 600 }}>{s.shop_name}</span>
                <div style={{ flex: 1, height: 8, borderRadius: 4, background: "#F3F4F6" }}>
                  <div style={{ height: "100%", borderRadius: 4, background: "#6366F1", width: `${Math.min(100, (s.order_count / (stats.total_orders || 1)) * 100)}%` }} />
                </div>
                <span style={{ fontSize: 13, color: "#6B7280", width: 60, textAlign: "right" }}>{s.order_count} cmd</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Onglet Logs ───────────────────────────────────────────────────────────────
function TabLogs({ shops }) {
  const [logs, setLogs]         = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [view, setView]         = useState("syncs"); // 'syncs' | 'webhooks'
  const [loading, setLoading]   = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [logsR, whR] = await Promise.all([
        api.get("/ecommerce/logs?limit=100"),
        api.get("/ecommerce/webhooks-log?limit=100"),
      ]);
      setLogs(logsR.data.logs || []);
      setWebhooks(whR.data.webhooks || []);
    } catch { setLogs([]); setWebhooks([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const data = view === "syncs" ? logs : webhooks;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <h2 style={{ margin: 0, fontSize: 18, flex: 1 }}>Logs & Monitoring</h2>
        <div style={{ display: "flex", gap: 4, padding: 4, background: "#F3F4F6", borderRadius: 8 }}>
          {[["syncs", "Syncs"], ["webhooks", "Webhooks"]].map(([v, l]) => (
            <button key={v} onClick={() => setView(v)} style={{ padding: "4px 12px", borderRadius: 6, border: "none", background: view === v ? "#fff" : "transparent", cursor: "pointer", fontSize: 13, fontWeight: view === v ? 600 : 400, boxShadow: view === v ? "0 1px 3px rgba(0,0,0,0.1)" : "none" }}>{l}</button>
          ))}
        </div>
        <button onClick={load} style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #D1D5DB", background: "#fff", cursor: "pointer" }}><RefreshCw size={14} /></button>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#9CA3AF" }}>Chargement...</div>
      ) : data.length === 0 ? (
        <div style={{ ...card, textAlign: "center", padding: 40 }}>
          <Activity size={40} color="#D1D5DB" style={{ marginBottom: 12 }} />
          <p style={{ color: "#6B7280", margin: 0 }}>Aucun log disponible</p>
        </div>
      ) : (
        <div style={{ ...card, overflow: "auto" }}>
          {view === "syncs" ? (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #F3F4F6" }}>
                  {["Boutique", "Type", "Direction", "Traités", "Échoués", "Durée", "Déclencheur", "Date"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 12px", color: "#6B7280", fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map(l => (
                  <tr key={l.id} style={{ borderBottom: "1px solid #F9FAFB" }}>
                    <td style={{ padding: "8px 12px" }}>{l.shop_name || `#${l.shop_id}`}</td>
                    <td style={{ padding: "8px 12px" }}><span style={{ padding: "1px 6px", borderRadius: 4, background: "#EDE9FE", color: "#7C3AED", fontSize: 11 }}>{l.sync_type}</span></td>
                    <td style={{ padding: "8px 12px", color: "#6B7280" }}>{l.direction}</td>
                    <td style={{ padding: "8px 12px", color: "#10B981", fontWeight: 600 }}>{l.records_processed}</td>
                    <td style={{ padding: "8px 12px", color: l.records_failed > 0 ? "#EF4444" : "#6B7280" }}>{l.records_failed}</td>
                    <td style={{ padding: "8px 12px", color: "#6B7280" }}>{l.duration_ms ? `${l.duration_ms}ms` : "—"}</td>
                    <td style={{ padding: "8px 12px" }}><span style={{ padding: "1px 6px", borderRadius: 4, background: "#F3F4F6", fontSize: 11 }}>{l.triggered_by}</span></td>
                    <td style={{ padding: "8px 12px", color: "#9CA3AF" }}>{fmtDate(l.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #F3F4F6" }}>
                  {["Boutique", "Plateforme", "Événement", "Hash payload", "Signature", "Traité", "IP", "Date"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 12px", color: "#6B7280", fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {webhooks.map(w => (
                  <tr key={w.id} style={{ borderBottom: "1px solid #F9FAFB" }}>
                    <td style={{ padding: "8px 12px" }}>{w.shop_name || `#${w.shop_id}`}</td>
                    <td style={{ padding: "8px 12px" }}>{w.platform}</td>
                    <td style={{ padding: "8px 12px" }}><span style={{ fontSize: 11 }}>{w.event_type}</span></td>
                    <td style={{ padding: "8px 12px", fontFamily: "monospace", fontSize: 11, color: "#6B7280" }}>{w.payload_hash ? w.payload_hash.slice(0, 16) + "…" : "—"}</td>
                    <td style={{ padding: "8px 12px" }}>
                      {w.signature_valid ? <CheckCircle size={14} color="#10B981" /> : <XCircle size={14} color="#EF4444" />}
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      {w.processed ? <CheckCircle size={14} color="#10B981" /> : <Clock size={14} color="#F59E0B" />}
                    </td>
                    <td style={{ padding: "8px 12px", fontSize: 11, color: "#9CA3AF" }}>{w.ip_address}</td>
                    <td style={{ padding: "8px 12px", color: "#9CA3AF" }}>{fmtDate(w.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────
export default function Ecommerce() {
  const { showToast } = useToast();
  const [tab, setTab]     = useState("dashboard");
  const [shops, setShops] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadShops = useCallback(async () => {
    try {
      const r = await api.get("/ecommerce/shops");
      setShops(r.data.shops || []);
    } catch { setShops([]); }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const r = await api.get("/ecommerce/stats");
      setStats(r.data);
    } catch { setStats(null); }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadShops(), loadStats()]).finally(() => setLoading(false));
  }, [loadShops, loadStats]);

  async function handleDelete(shopId, shopName) {
    if (!window.confirm(`Déconnecter la boutique "${shopName}" ? Les données importées seront conservées.`)) return;
    try {
      await api.delete(`/ecommerce/shops/${shopId}`);
      showToast("Boutique déconnectée", "success");
      loadShops();
      loadStats();
    } catch { showToast("Erreur lors de la déconnexion", "error"); }
  }

  async function handleSync(shopId, type = "orders") {
    try {
      await api.post(`/ecommerce/shops/${shopId}/sync/${type}`);
      showToast(`Synchronisation ${type} lancée`, "success");
      loadStats();
    } catch (err) {
      const msg = err.response?.data?.error || "Synchronisation échouée";
      showToast(msg, "error");
    }
  }

  async function handleTest(shopId) {
    try {
      const r = await api.post(`/ecommerce/shops/${shopId}/test`);
      if (r.data.ok) { showToast("Connexion OK ✓", "success"); loadShops(); }
      else showToast(r.data.error || "Connexion échouée", "error");
    } catch { showToast("Erreur lors du test", "error"); }
  }

  async function handleSyncAll() {
    for (const shop of shops.filter(s => s.status === "connecté")) {
      await handleSync(shop.id, "orders");
    }
  }

  return (
    <div style={{ padding: "24px", maxWidth: 1200, margin: "0 auto" }}>
      {/* En-tête */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: "linear-gradient(135deg, #6366F1, #8B5CF6)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <ShoppingBag size={22} color="#fff" />
        </div>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>E-commerce</h1>
          <p style={{ margin: 0, fontSize: 13, color: "#6B7280" }}>Connecteurs Shopify · WooCommerce · PrestaShop</p>
        </div>
      </div>

      {/* Onglets */}
      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--border, #e5e7eb)", marginBottom: 24 }}>
        {TABS.map(({ id, label, Icon }) => (
          <button key={id} onClick={() => setTab(id)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 16px", border: "none", background: "none", cursor: "pointer", fontWeight: tab === id ? 700 : 400, color: tab === id ? "#6366F1" : "#6B7280", borderBottom: tab === id ? "2px solid #6366F1" : "2px solid transparent", fontSize: 14, transition: "all 0.15s" }}>
            <Icon size={16} /> {label}
          </button>
        ))}
      </div>

      {/* Contenu */}
      {tab === "dashboard" && <TabDashboard stats={stats} shops={shops} onSyncAll={handleSyncAll} />}
      {tab === "shops"     && <TabShops shops={shops} onRefresh={() => { loadShops(); loadStats(); }} onDelete={handleDelete} onSync={handleSync} onTest={handleTest} />}
      {tab === "orders"    && <TabOrders shops={shops} />}
      {tab === "products"  && <TabProducts shops={shops} />}
      {tab === "stats"     && <TabStats stats={stats} />}
      {tab === "logs"      && <TabLogs shops={shops} />}
    </div>
  );
}
